/**
 * Форматы, которые принимает файловый сервис (ADR-042). Формат определяется по сигнатуре
 * содержимого, а не по имени файла; расширения нужны только для `accept` в браузере.
 * SVG, HTML, HEIC и исполняемые форматы не принимаются.
 */
export const FILE_FORMATS = {
    jpeg: { mime: 'image/jpeg', extensions: ['jpg', 'jpeg'] },
    png: { mime: 'image/png', extensions: ['png'] },
    webp: { mime: 'image/webp', extensions: ['webp'] },
    tiff: { mime: 'image/tiff', extensions: ['tif', 'tiff'] },
    pdf: { mime: 'application/pdf', extensions: ['pdf'] },
    /** Таблицы импорта и выгрузки (ADR-052): превью не строятся. */
    xlsx: {
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        extensions: ['xlsx'],
    },
    csv: { mime: 'text/csv', extensions: ['csv'] },
};
/** Форматы-документы: файл проверяется и хранится, но превью у него нет. */
export const DOCUMENT_FORMATS = ['xlsx', 'csv'];
export const FILE_FORMAT_KEYS = Object.keys(FILE_FORMATS);
/**
 * Назначения файлов: кто загружает, какие форматы, где лежит. Phase 1a добавит файлы заказов.
 * Бакет назначения выбирает API (`apps/api/src/files/purposes.ts`).
 */
export const FILE_PURPOSES = {
    /** Логотип инстанса: «Настройки» → «Бренд», меню и страница входа. */
    branding_logo: {
        formats: ['png', 'jpeg', 'webp'],
        maxSizeMb: 5,
        visibility: 'public',
        upload: 'settings.manage',
        view: null,
        download: 'settings.manage',
        logDownloads: false,
    },
    /** Проверка загрузки и обработки на странице «Состояние»; файлы удаляются через сутки. */
    diagnostics: {
        formats: ['jpeg', 'png', 'webp', 'tiff', 'pdf'],
        visibility: 'private',
        upload: 'system.diagnostics',
        view: 'system.diagnostics',
        download: 'system.diagnostics',
        logDownloads: true,
        ttlHours: 24,
    },
    /** Исходный файл импорта прайса или клиентов (ADR-052); удаляется через 7 дней. */
    import_source: {
        formats: ['xlsx', 'csv'],
        maxSizeMb: 20,
        visibility: 'private',
        // Загружает и видит карточку (имя, размер, статус) любой сотрудник с кассовыми правами;
        // право на сам импорт проверяет задание. Скачать исходник (в нём бывают персональные
        // данные клиентов) — только с правом на клиентов.
        upload: 'order.view',
        view: null,
        download: 'customer.edit',
        logDownloads: true,
        ttlHours: 7 * 24,
    },
    /** Выгрузка прайса в Excel для правки и обратного импорта; удаляется через сутки. */
    price_export: {
        formats: ['xlsx'],
        maxSizeMb: 20,
        visibility: 'private',
        upload: 'pricing.manage',
        view: 'pricing.manage',
        download: 'pricing.manage',
        logDownloads: false,
        ttlHours: 24,
    },
    /**
     * Файлы заказа (Phase 1a, CP5 №8): оригиналы клиента, макет, файл для печати, вложения.
     * Права — по точкам заказа; скачивание оригинала журналируется.
     */
    order_file: {
        formats: ['jpeg', 'png', 'webp', 'tiff', 'pdf'],
        visibility: 'private',
        upload: 'file.upload',
        view: 'file.view_preview',
        download: 'file.download.original',
        logDownloads: true,
        scope: 'points',
    },
    /**
     * Документы для печати (ADR-056): чек, этикетка, тестовая печать. Создаются сервером из
     * снимка в БД при каждой печати и удаляются через сутки. Загрузка из браузера не используется.
     */
    print_document: {
        formats: ['pdf'],
        maxSizeMb: 10,
        visibility: 'private',
        upload: 'settings.manage',
        view: null,
        download: 'order.view',
        logDownloads: false,
        ttlHours: 24,
        ownerAccess: true,
        inline: true,
    },
};
export const FILE_PURPOSE_KEYS = Object.keys(FILE_PURPOSES);
export const FILE_STATUSES = ['uploading', 'processing', 'ready', 'rejected', 'failed'];
/**
 * Почему файл не принят (`rejected`, проверка при завершении загрузки) или не обработан
 * (`failed`, worker-media). Фронты показывают текст по коду.
 */
export const FILE_FAILURE_REASONS = [
    'too_large',
    'format_not_allowed',
    'pixel_limit',
    'corrupt',
    'encrypted',
    'timeout',
    'processing_error',
];
/** Части multipart-загрузки: S3 требует не меньше 5 МиБ (кроме последней) и не больше 10 000 частей. */
export const UPLOAD_MIN_PART_SIZE = 8 * 1024 * 1024;
export const UPLOAD_MAX_PARTS = 10_000;
export function uploadPartSize(size) {
    return Math.max(UPLOAD_MIN_PART_SIZE, Math.ceil(size / UPLOAD_MAX_PARTS));
}
/** Значение `accept` для `<input type="file">`: MIME и расширения форматов назначения. */
export function acceptForPurpose(purpose) {
    return FILE_PURPOSES[purpose].formats
        .flatMap((format) => [
        FILE_FORMATS[format].mime,
        ...FILE_FORMATS[format].extensions.map((ext) => `.${ext}`),
    ])
        .join(',');
}
//# sourceMappingURL=file-purposes.js.map